import React from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useMusicPlayer } from '../../MusicPlayerContext';
import { useUser } from '../../UserContext';
import { AdminContext } from '../../AdminContext';
import { getFreeMemberNewSongInfo } from '../../utils/playbackGate';
import { showUiHint, showHintPlus } from '../../utils/uiHint';
import { getCoverImageUrl, hasVideo } from '../../utils/musicStreamUrls';
import { isInstrumentalMusic, normalizeInstrumentalTitle } from '../../utils/musicMeta';
import { spaNavigate } from '../../utils/spaNavigate';
import HomeGlassCard from './HomeGlassCard';
import { buttonClass, theme } from '../../ui/theme';
import { limitPlayAll } from '../../utils/playAllLimit';

function getTitleByLang(music, lang) {
  if (!music) return '';
  const title = (() => {
    switch (lang) {
      case 'cn':
        return music.title_zh_cn || music.title || '';
      case 'tw':
        return music.title_zh_tw || music.title || '';
      case 'ja':
        return music.title_ja || music.title || '';
      case 'en':
        return music.title_en || music.title || '';
      default:
        return music.title || '';
    }
  })();
  return normalizeInstrumentalTitle(music, title);
}

const LATEST_PREVIEW_PAGE_SIZE = 6; // 最多显示 6 首

export default function HomeLatestPreview({ currentLang = 'ja', onOpenAll = null }) {
  const { i18n, t } = useTranslation();
  const { user } = useUser();
  const { isAdmin } = React.useContext(AdminContext);
  const { playMusicNow, primePlayMusic, setCurrentMusic, addToPlaylist, setPlaying } = useMusicPlayer();

  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const lang = i18n?.language || currentLang || 'ja';
  const btnOutlineSm = buttonClass({ variant: 'outline', size: 'sm' });

  const fetchLatest = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get('/api/music/list', {
        params: {
          page: 1,
          pageSize: LATEST_PREVIEW_PAGE_SIZE,
          sort: 'latest'
        }
      });
      if (res?.data?.success) {
        setItems(Array.isArray(res.data.data?.musics) ? res.data.data.musics : []);
      } else {
        setError(res?.data?.error || t('musicList.fetchFailed', '获取音乐列表失败'));
      }
    } catch (e) {
      setError(e?.response?.data?.error || t('musicList.fetchFailed', '获取音乐列表失败'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  React.useEffect(() => {
    fetchLatest();
  }, [fetchLatest]);

  const handlePlay = React.useCallback(
    (music, e) => {
      try {
        e?.stopPropagation?.();
      } catch (_) {}
      if (!music) return;
      const gate = getFreeMemberNewSongInfo({ music, user, isAdmin: !!isAdmin });
      if (gate?.blocked) {
        showUiHint(t('musicList.freeMemberNewSongHint', { days: Number(gate?.daysLeft || 0) }), {
          duration: 2200
        });
        return;
      }
      playMusicNow(music);
    },
    [user, isAdmin, t, playMusicNow]
  );

  const handleOpenDetail = React.useCallback(
    (music, e) => {
      try {
        e?.stopPropagation?.();
      } catch (_) {}
      if (!music?.id) return;
      spaNavigate(`/${lang}/music/${music.id}.html`);
    },
    [lang]
  );

  const handlePlayAll = React.useCallback(
    (e) => {
      try {
        e?.stopPropagation?.();
      } catch (_) {}
      if (!items || items.length === 0) return;
      if (!user && !isAdmin) {
        showHintPlus(t('musicList.guestContinuousPlayHint'));
        return;
      }
      const playable = [];
      let firstBlocked = null;
      items.forEach((music) => {
        const gate = getFreeMemberNewSongInfo({ music, user, isAdmin: !!isAdmin });
        if (gate?.blocked) {
          if (!firstBlocked) firstBlocked = gate;
          return;
        }
        playable.push(music);
      });
      if (playable.length === 0) {
        if (firstBlocked) {
          showUiHint(t('musicList.freeMemberNewSongHint', { days: Number(firstBlocked?.daysLeft || 0) }), {
            duration: 2200
          });
        }
        return;
      }
      const limited = limitPlayAll(playable);
      limited.forEach((music) => addToPlaylist(music));
      setCurrentMusic(limited[0]);
      setPlaying(true);
      primePlayMusic(limited[0]);
    },
    [items, user, isAdmin, t, addToPlaylist, setCurrentMusic, setPlaying, primePlayMusic]
  );

  return (
    <HomeGlassCard>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-white text-lg font-semibold">{t('homeLanding.latestPreviewTitle', '最近更新')}</div>
          <div className="text-xs text-white/60 mt-1 whitespace-nowrap">{t('homeLanding.latestPreviewDesc', '看看新上架的作品，遇见新的旋律。')}</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            className={[btnOutlineSm, 'whitespace-nowrap'].join(' ')}
            onClick={handlePlayAll}
            disabled={loading || !!error || items.length === 0}
          >
            {t('homeLanding.latestPreviewPlayAll', '播放全部')}
          </button>
          <button
            type="button"
            className={[theme.links.subtleUnderline, 'whitespace-nowrap'].join(' ')}
            onClick={() => (onOpenAll ? onOpenAll() : null)}
          >
            {t('homeLanding.latestPreviewMore', '查看全部新上架')}
          </button>
        </div>
      </div>

      <div className="mt-3">
        {loading ? (
          <div className="text-sm text-white/70">{t('musicList.loading', '加载中...')}</div>
        ) : error ? (
          <div className="text-sm text-red-200 flex items-center justify-between gap-3">
            <div className="truncate">{error}</div>
            <button
              type="button"
              className={btnOutlineSm}
              onClick={fetchLatest}
            >
              {t('musicRankings.retry', '重试')}
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className="text-sm text-white/70">{t('musicList.noMusic', '暂无音乐')}</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {items.map((music) => {
              const title = getTitleByLang(music, lang);
              const cover = getCoverImageUrl(music) || (hasVideo(music) ? '/icons/mtv.png' : '');
              return (
                <div
                  key={music.id}
                  className={[
                    'rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-all group border cursor-pointer',
                    theme.surfaces.glassItem,
                    'border-white/10'
                  ].join(' ')}
                  onClick={(e) => handleOpenDetail(music, e)}
                >
                  <div className="relative aspect-square bg-white/[0.04] border border-white/10 overflow-hidden">
                    {cover ? (
                      <img src={cover} alt={title} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white/50 text-xs">No Cover</div>
                    )}
                    <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          className={buttonClass({
                            variant: 'outline',
                            size: 'xs',
                            className: 'bg-blue-500/30 border-blue-400/25 hover:bg-blue-500/40'
                          })}
                          onClick={(e) => handlePlay(music, e)}
                        >
                          {t('musicList.play', '播放')}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="p-2">
                    <div className="font-semibold text-sm text-white truncate text-left inline-flex items-center gap-1">
                      {hasVideo(music) && (
                        <img
                          src="/icons/mtv-s.png"
                          alt="MTV"
                          className="w-4 h-4 flex-shrink-0 object-contain"
                          title={t('player.hasVideo') || '视频'}
                        />
                      )}
                      {!hasVideo(music) && isInstrumentalMusic(music) && (
                        <img
                          src="/icons/mcd-s.png"
                          alt={t('musicList.categoryInstrumental', '纯音乐')}
                          className="w-4 h-4 flex-shrink-0 object-contain"
                          title={t('musicList.categoryInstrumental', '纯音乐')}
                        />
                      )}
                      <span className="truncate">{title || t('musicList.tableTitle', '标题')}</span>
                    </div>
                    <div className="mt-1 text-xs text-white/55 truncate text-left">{music.album || ' '}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </HomeGlassCard>
  );
}

